// Organization Status Mapping Utility
// Maps between user-friendly display names and database values for organization statuses

interface OrganizationStatusMapping {
  displayName: string;
  dbValue: string;
  category: string;
  description: string;
}

// Comprehensive organization status mappings
const organizationStatusMappings: OrganizationStatusMapping[] = [
  // Active Status
  {
    displayName: "Active",
    dbValue: "active",
    category: "Operational",
    description: "Organization is currently active and operational",
  },

  // Planning Status
  {
    displayName: "Planned",
    dbValue: "planned",
    category: "Development",
    description: "Organization is in planning phase",
  },

  // Inactive Statuses
  {
    displayName: "Insolvent",
    dbValue: "insolvent",
    category: "Inactive",
    description: "Organization is insolvent",
  },
  {
    displayName: "Dissolved",
    dbValue: "dissolved",
    category: "Inactive",
    description: "Organization has been dissolved",
  },

  // Unknown Status
  {
    displayName: "Unknown",
    dbValue: "unknown",
    category: "Unknown",
    description: "Organization status is unknown",
  },
];

// Create lookup maps for efficient conversion
const displayNameToDbValue = new Map<string, string>();
const dbValueToDisplayName = new Map<string, string>();
const categoryMap = new Map<string, OrganizationStatusMapping[]>();

// Initialize lookup maps
organizationStatusMappings.forEach((mapping) => {
  displayNameToDbValue.set(mapping.displayName, mapping.dbValue);
  dbValueToDisplayName.set(mapping.dbValue, mapping.displayName);

  if (!categoryMap.has(mapping.category)) {
    categoryMap.set(mapping.category, []);
  }
  categoryMap.get(mapping.category)!.push(mapping);
});

// Export functions for converting between display names and database values

/**
 * Get all organization status display names
 */
export const getAllOrganizationStatusDisplayNames = (): string[] => {
  return organizationStatusMappings.map((mapping) => mapping.displayName);
};

/**
 * Get all organization status database values
 */
export const getAllOrganizationStatusDbValues = (): string[] => {
  return organizationStatusMappings.map((mapping) => mapping.dbValue);
};

/**
 * Convert display name to database value
 */
export const getDbValueFromDisplayName = (displayName: string): string => {
  const dbValue = displayNameToDbValue.get(displayName);
  if (!dbValue) {
    throw new Error(`Unknown organization status display name: ${displayName}`);
  }
  return dbValue;
};

/**
 * Convert database value to display name
 */
export const getDisplayNameFromDbValue = (dbValue: string): string => {
  const displayName = dbValueToDisplayName.get(dbValue);
  if (!displayName) {
    throw new Error(`Unknown organization status database value: ${dbValue}`);
  }
  return displayName;
};

/**
 * Convert array of display names to database values
 */
export const getDbValuesFromDisplayNames = (
  displayNames: string[]
): string[] => {
  return displayNames.map((name) => getDbValueFromDisplayName(name));
};

/**
 * Convert array of database values to display names
 */
export const getDisplayNamesFromDbValues = (dbValues: string[]): string[] => {
  return dbValues.map((value) => getDisplayNameFromDbValue(value));
};

/**
 * Get organization status mappings by category
 */
export const getOrganizationStatusesByCategory = (
  category: string
): OrganizationStatusMapping[] => {
  return categoryMap.get(category) || [];
};

/**
 * Get all categories
 */
export const getOrganizationStatusCategories = (): string[] => {
  return Array.from(categoryMap.keys());
};

/**
 * Get organization status mapping by display name
 */
export const getOrganizationStatusMapping = (
  displayName: string
): OrganizationStatusMapping | undefined => {
  return organizationStatusMappings.find(
    (mapping) => mapping.displayName === displayName
  );
};

/**
 * Check if an organization status matches selected statuses
 */
export const organizationMatchesStatuses = (
  organizationStatuses: string[],
  selectedDisplayNames: string[]
): boolean => {
  if (selectedDisplayNames.length === 0) return true;

  const selectedDbValues = getDbValuesFromDisplayNames(selectedDisplayNames);
  return organizationStatuses.some((status) =>
    selectedDbValues.includes(status)
  );
};

/**
 * Get category for a database value
 */
export const getCategoryForDbValue = (dbValue: string): string | undefined => {
  const mapping = organizationStatusMappings.find((m) => m.dbValue === dbValue);
  return mapping?.category;
};

/**
 * Get description for a database value
 */
export const getDescriptionForDbValue = (
  dbValue: string
): string | undefined => {
  const mapping = organizationStatusMappings.find((m) => m.dbValue === dbValue);
  return mapping?.description;
};

/**
 * Get color for organization status (for chip display)
 */
export const getColorForStatus = (
  dbValue: string
): "success" | "warning" | "error" | "default" => {
  switch (dbValue) {
    case "active":
      return "success";
    case "planned":
      return "warning";
    case "insolvent":
    case "dissolved":
      return "error";
    case "unknown":
    default:
      return "default";
  }
};
